import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RestockingRule, RestockingRulePayload } from 'apis/restocking-rules/types';

import { RestockingRuleFormValues } from './RestockingRuleFields';

const WEEKDAY_FREQUENCIES: RestockingRuleFormValues['frequency'][] = [
  'WEEKLY',
  'EVERY_TWO_WEEKS',
  'EVERY_THREE_WEEKS',
  'EVERY_FOUR_WEEKS',
];

const buildRestockingRuleSchedulePayload = (
  values: RestockingRuleFormValues,
  timezone: string,
): RestockingRulePayload['schedule'] => {
  switch (values.frequency) {
    case 'DAILY': {
      return {
        byHour: Math.trunc(values.timeInMinutes / 60),
        byMinute: values.timeInMinutes % 60,
        interval: 1,
        frequency: 'DAILY',
        startDate: values.startDate.toISOString(),
        type: 'TIME_BASED',
        timezone,
      };
    }
    case 'WEEKLY':
    case 'EVERY_TWO_WEEKS':
    case 'EVERY_THREE_WEEKS':
    case 'EVERY_FOUR_WEEKS': {
      const interval = WEEKDAY_FREQUENCIES.indexOf(values.frequency) + 1;

      return {
        byDay: values.weekdays,
        byHour: Math.trunc(values.timeInMinutes / 60),
        byMinute: values.timeInMinutes % 60,
        frequency: 'WEEKLY',
        interval,
        startDate: values.startDate.toISOString(),
        type: 'TIME_BASED',
        timezone,
      };
    }
    case 'SOLD_OUT_FOR_X_DAY': {
      if (values.applyAfterDays === undefined) {
        throw new Error('applyAfterDays is required');
      }

      return {
        type: 'STATE_BASED',
        dayCount: values.applyAfterDays,
        state: 'SOLD_OUT',
        byHour: Math.trunc(values.timeInMinutes / 60),
        byMinute: values.timeInMinutes % 60,
        timezone,
      };
    }

    default:
      throw new Error('Unknown restock frequency');
  }
};

export const buildRestockingRulePayload = (
  values: RestockingRuleFormValues,
  categories: Category[],
  menuId: string,
  timezone: string,
): RestockingRulePayload => {
  const parentCategoryByIdRecord = categories.reduce<Record<string, Category>>(
    (record, category) => ({ ...record, [category.id]: category }),
    {},
  );

  const filters = [
    ...values.categoryIds.map<RestockingRulePayload['filters'][number]>(categoryId => ({
      type: !!parentCategoryByIdRecord[categoryId] ? 'CATEGORY' : 'SUB_CATEGORY',
      identifier: categoryId,
    })),
    ...values.productFamilyIds.map<RestockingRulePayload['filters'][number]>(productFamilyId => ({
      type: 'PRODUCT_HIERARCHY',
      identifier: productFamilyId,
    })),
  ];

  return {
    menuId,
    name: values.ruleName.trim(),
    type: values.type,
    filters,
    schedule: buildRestockingRuleSchedulePayload(values, timezone),
  };
};

export const buildFrequencyFormValues = (
  data: RestockingRule['schedule'],
): Pick<RestockingRuleFormValues, 'frequency' | 'weekdays' | 'applyAfterDays' | 'startDate'> => {
  switch (data.type) {
    case 'STATE_BASED': {
      return {
        frequency: 'SOLD_OUT_FOR_X_DAY',
        weekdays: [],
        applyAfterDays: data.dayCount,
        startDate: new Date(),
      };
    }
    case 'TIME_BASED': {
      return {
        frequency: data.frequency === 'WEEKLY' ? WEEKDAY_FREQUENCIES[data.interval - 1] : 'DAILY',
        weekdays: data.frequency === 'WEEKLY' ? data.byDay : [],
        applyAfterDays: 0,
        startDate: new Date(data.startDate),
      };
    }

    default: {
      throw new Error('Unknown schedule type');
    }
  }
};

export const buildRestockingRuleFormValues = (data: RestockingRule): RestockingRuleFormValues => ({
  ruleName: data.name,
  categoryIds: data.filters.reduce<string[]>(
    (res, cur) => res.concat(cur.type === 'SUB_CATEGORY' || cur.type === 'CATEGORY' ? cur.identifier : []),
    [],
  ),
  productFamilyIds: data.filters.reduce<string[]>(
    (res, cur) => res.concat(cur.type === 'PRODUCT_HIERARCHY' ? cur.identifier : []),
    [],
  ),
  type: data.type,
  timeInMinutes: data.schedule.byHour * 60 + data.schedule.byMinute,
  ...buildFrequencyFormValues(data.schedule),
});
